import { useMemo } from 'react'
import { SystemSettings } from '../../../../shared/useSystemSettings'

type FlagItem = {
  value: boolean
  description: string
}

export type FlagsGroup = {
  groupName: string
  flags: Record<string, FlagItem>
}

type FeatureFlagsGroup = {
  groupName: string
  type: 'featureFlags'
  flags: Record<string, FlagItem>
}

type SystemSettingsGroup = {
  groupName: string
  type: 'systemSettings'
  flags: Record<
    string,
    {
      value: boolean | undefined
      description: string
    }
  >
}

type FlagsSection = {
  [key: string]: FeatureFlagsGroup | SystemSettingsGroup
}

export const useMapFeatureFlagsAndSystemSettings = (
  flags: Record<string, boolean>,
  systemSettings: SystemSettings | undefined,
): FlagsSection[] => {
  return useMemo(
    () => [
      {
        organizationSettings: {
          groupName: 'Organisation Settings',
          type: 'featureFlags',
          flags: {
            isPublicOrg: {
              value: flags.isPublicOrg,
              description:
                'Determines whether an org should be treated as a public or private org',
            },
            isArchiveOrg: {
              value: flags.isArchiveOrg,
              description:
                'Removes stuff not available for archive orgs (most stuff)',
            },
          },
        },
        addOnModules: {
          groupName: 'Add-On Modules',
          type: 'featureFlags',
          flags: {
            enableQualityAssurance: {
              value: flags.enableQualityAssurance,
              description: 'Enables quality assurance features',
            },
          },
        },
        audioSettings: {
          groupName: 'Audio Settings',
          type: 'systemSettings',
          flags: {
            obfuscateSpeech: {
              value: systemSettings?.speech.obfuscateSpeech,
              description: 'Controls speech obfuscation for privacy purposes',
            },
            anonymizeAsrData: {
              value: systemSettings?.deletionStrategy.anonymizeAsrData,
              description: 'Anonymizes ASR data (soft delete)',
            },
            deleteCustomer: {
              value: systemSettings?.deletionStrategy.deleteCustomer,
              description: 'Deletes customer data (audio track)',
            },
          },
        },

        generalFeatureSettings: {
          groupName: 'General Feature Settings',
          type: 'featureFlags',
          flags: {
            enableSentimentFeature: {
              value: flags.enableSentimentFeature,
              description: 'Enable Sentiment feature',
            },
            enableMasterTracker: {
              value: flags.enableMasterTracker,
              description:
                'Determines whether the user has access to master trackers',
            },
            enableProbability: {
              value: flags.enableProbability,
              description: 'Enable probability in tracker configuration',
            },
            showTranscriptionDownloadButtons: {
              value: flags.showTranscriptionDownloadButtons,
              description:
                "Controls whether or not 'Download transcription' buttons will be visible in Conversation page",
            },
          },
        },
        filterSettings: {
          groupName: 'Filter Settings',
          type: 'featureFlags',
          flags: {
            showExternalId: {
              value: flags.showExternalId,
              description: 'Enable External ID segmentation filter',
            },
            useSpeakersFilter: {
              value: flags.useSpeakersFilter,
              description:
                'Determines whether the `Speakers` filter should be visible',
            },
          },
        },
        outboundCustomersSettings: {
          groupName: 'Settings for Outbound customers',
          type: 'featureFlags',
          flags: {
            employeeAsDefaultSpeakerTab: {
              value: flags.employeeAsDefaultSpeakerTab,
              description: 'Default select employee speaker tab',
            },
            hideHomepage: {
              value: flags.hideHomepage,
              description:
                'Determines whether to hide homepage for sales-oriented organizations',
            },
          },
        },
        textSettings: {
          groupName: 'Text Settings',
          type: 'featureFlags',
          flags: {
            enableText: {
              value: flags.enableText,
              description: 'Enables text/cases/emails pages and filters',
            },
            useEmailChannelAsDefault: {
              value: flags.useEmailChannelAsDefault,
              description:
                'On pages where the user can select a channel, prefer text channel as default',
            },
            isChatOrg: {
              value: flags.isChatOrg,
              description: 'Removes stuff not available for chat',
            },
          },
        },
        copilotSettings: {
          groupName: 'Copilot Settings',
          type: 'featureFlags',
          flags: {
            copilot_enabled: {
              value: flags.copilot_enabled,
              description: 'Enable Copilot',
            },
            copilot_previousInteractions: {
              value: flags.copilot_previousInteractions,
              description: 'Enable Copilot previous interactions',
            },
            copilot_askAi: {
              value: flags.copilot_askAi,
              description: 'Enable Copilot ask AI',
            },
            copilot_liveSummary: {
              value: flags.copilot_liveSummary,
              description: 'Enable Copilot live summary',
            },
            copilot_coPilotOnly: {
              value: flags.copilot_coPilotOnly,
              description: 'Enable Copilot only',
            },
          },
        },
        deprecatedOrCustomerSpecificSettings: {
          groupName: 'Deprecated or Customer specific settings',
          type: 'featureFlags',
          flags: {
            showStatus: {
              value: flags.showStatus,
              description: 'Enable Intercom guided tours',
            },
            enableIntercom: {
              value: flags.enableIntercom,
              description: 'Enable intercom widget + tracking',
            },
            hideCustomerInsightsFromPlainUsers: {
              value: flags.hideCustomerInsightsFromPlainUsers,
              description: 'Only enabled for Velkommen',
            },
            enableScoring: {
              value: flags.enableScoring,
              description: 'Enables scoring features',
            },
            _Velkommen_trackerExampleCommentPrefixFeature: {
              value: flags._Velkommen_trackerExampleCommentPrefixFeature,
              description: 'Only enabled for Velkommen',
            },
          },
        },
      },
    ],
    [flags, systemSettings],
  )
}
